<template>
  <div class="shared-proforma-fields">
    <section class="report-section">
      <h3>{{ $t('clinical-information-section') }}</h3>
      <div class="field">
        <label class="label">{{ $t('history-previous-urinary-disease-therapy-ce') }}</label>
        <div class="control">
          <textarea class="textarea" rows="3" v-model="report.clinical.history" :maxlength="10000"></textarea>
          <p class="help has-text-right">{{ report.clinical.history.length }} / 10000</p>
        </div>
      </div>
      <div class="field">
        <label class="label">{{ $t('specimen-site-proforma') }}</label>
        <div class="checkbox-grid">
          <label v-for="option in specimenSiteOptions" :key="option.value" class="checkbox report-checkbox">
            <input
              type="checkbox"
              :value="option.value"
              :checked="report.clinical.specimenSites.includes(option.value)"
              @change="toggleCheckbox(report.clinical.specimenSites, option.value, $event.target.checked)"
            >
            <span>{{ option.label }}</span>
          </label>
        </div>
      </div>
      <div v-if="report.clinical.specimenSites.includes('other')" class="field" data-testid="specimen-site-other-field">
        <label class="label">{{ $t('specimen-site-other-specify') }}</label>
        <div class="control">
          <textarea class="textarea" rows="2" v-model="report.clinical.specimenSiteOther" :maxlength="5000"></textarea>
          <p class="help has-text-right">{{ report.clinical.specimenSiteOther.length }} / 5000</p>
        </div>
      </div>
      <div class="field">
        <label class="label">{{ $t('operative-procedure-proforma') }}</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select v-model="report.clinical.operativeProcedure">
              <option v-for="option in operativeProcedureOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div v-if="report.clinical.operativeProcedure === 'other'" class="field">
        <label class="label">{{ $t('other-specify') }}</label>
        <div class="control">
          <textarea class="textarea" rows="2" v-model="report.clinical.operativeProcedureOther" :maxlength="5000"></textarea>
          <p class="help has-text-right">{{ report.clinical.operativeProcedureOther.length }} / 5000</p>
        </div>
      </div>
    </section>

    <section class="report-section">
      <h3>{{ $t('macroscopic-finding-section') }}</h3>
      <div class="field">
        <label class="label">{{ $t('submitted-tissue-description') }}</label>
        <div class="control">
          <textarea class="textarea" rows="4" v-model="report.macroscopic.description" :maxlength="15000"></textarea>
          <p class="help has-text-right">{{ report.macroscopic.description.length }} / 15000</p>
        </div>
      </div>
      <div class="field" data-testid="measurement-structured-field">
        <div class="field-heading">
          <label class="label">{{ $t('measurement') }}</label>
          <button class="button is-small add-measurement-button" type="button" @click="addMeasurementItem">
            {{ $t('measurement-add-item') }}
          </button>
        </div>
        <p class="help measurement-help">{{ $t('measurement-repeat-help') }}</p>
        <div class="measurement-summary" data-testid="measurement-fragment-count">
          <span class="small-label">{{ $t('measurement-fragment-count') }}</span>
          <strong>{{ report.macroscopic.measurement.items.length }}</strong>
        </div>
        <div class="measurement-items">
          <div
            v-for="(item, index) in report.macroscopic.measurement.items"
            :key="index"
            class="measurement-item"
            data-testid="measurement-item"
          >
            <div class="measurement-item-header">
              <span>{{ $t('measurement-item') }} {{ index + 1 }}</span>
              <button
                v-if="report.macroscopic.measurement.items.length > 1"
                class="button is-small remove-measurement-button"
                type="button"
                @click="removeMeasurementItem(index)"
              >
                {{ $t('button-remove') }}
              </button>
            </div>
            <div class="structured-grid measurement-grid">
              <div class="field">
                <label class="small-label">{{ $t('measurement-length') }}</label>
                <input class="input" type="number" min="0" step="0.1" v-model="item.length">
              </div>
              <div class="field">
                <label class="small-label">{{ $t('measurement-width') }}</label>
                <input class="input" type="number" min="0" step="0.1" v-model="item.width">
              </div>
              <div class="field">
                <label class="small-label">{{ $t('measurement-height') }}</label>
                <input class="input" type="number" min="0" step="0.1" v-model="item.height">
              </div>
              <div class="field measurement-unit-field">
                <label class="small-label">{{ $t('measurement-unit') }}</label>
                <span class="unit-chip">cm</span>
              </div>
            </div>
            <div class="field">
              <label class="small-label">{{ $t('measurement-note') }}</label>
              <textarea class="textarea" rows="2" v-model="item.note" :maxlength="5000"></textarea>
            </div>
          </div>
        </div>
      </div>
      <div class="field" data-testid="weight-structured-field">
        <label class="label">{{ $t('weight') }}</label>
        <div class="structured-grid weight-grid">
          <div class="field">
            <label class="small-label">{{ $t('weight-status') }}</label>
            <div class="select is-fullwidth">
              <select v-model="report.macroscopic.weight.status">
                <option v-for="option in weightStatusOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
          <div class="field">
            <label class="small-label">{{ $t('weight-value') }}</label>
            <div class="input-with-unit">
              <input
                class="input"
                type="number"
                min="0"
                step="0.1"
                v-model="report.macroscopic.weight.value"
                :disabled="report.macroscopic.weight.status !== 'measured'"
              >
              <span class="unit-chip">g</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="report-section">
      <h3>{{ $t('microscopic-finding-section') }}</h3>
      <div class="field" data-testid="histological-tumour-type">
        <label class="label">{{ $t('histological-tumour-type') }}</label>
        <div class="tumour-type-tree">
          <div v-for="group in tumourTypeGroups" :key="group.key" class="tumour-type-group">
            <div class="tumour-type-group-label" :data-testid="`tumour-group-${group.key}`">{{ group.label }}</div>
            <div class="checkbox-grid tumour-type-children">
              <label v-for="option in group.children" :key="option.value" class="radio report-checkbox">
                <input
                  type="radio"
                  name="histological-tumour-type"
                  :value="option.value"
                  v-model="report.microscopic.tumourType"
                >
                <span>{{ option.label }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div v-if="report.microscopic.tumourType === 'other'" class="field">
        <label class="label">{{ $t('other-specify') }}</label>
        <div class="control">
          <textarea class="textarea" rows="2" v-model="report.microscopic.tumourTypeOther" :maxlength="5000"></textarea>
          <p class="help has-text-right">{{ report.microscopic.tumourTypeOther.length }} / 5000</p>
        </div>
      </div>
      <div v-if="showTumourTypeDetail && report.microscopic.tumourType === 'benign-condition'" class="field" data-testid="benign-condition-detail-field">
        <label class="label">{{ $t('benign-condition-detail') }}</label>
        <div class="control">
          <textarea class="textarea" rows="2" v-model="report.microscopic.tumourTypeDetail" :maxlength="5000"></textarea>
          <p class="help has-text-right">{{ (report.microscopic.tumourTypeDetail || '').length }} / 5000</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { optionPairs, translateOptions, translateTumourTypeGroups } from './reportTemplateOptions';
import { createMeasurementItem } from './reportTemplateText';

export default {
  name: 'SharedProformaFields',
  props: {
    report: {
      type: Object,
      required: true
    },
    showTumourTypeDetail: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    operativeProcedureOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.operativeProcedure);
    },
    specimenSiteOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.specimenSite);
    },
    tumourTypeGroups() {
      return translateTumourTypeGroups(this.$t.bind(this));
    },
    weightStatusOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.weightStatus);
    }
  },
  methods: {
    addMeasurementItem() {
      this.report.macroscopic.measurement.items.push(createMeasurementItem());
    },
    removeMeasurementItem(index) {
      if (this.report.macroscopic.measurement.items.length > 1) {
        this.report.macroscopic.measurement.items.splice(index, 1);
      }
    },
    toggleCheckbox(target, value, checked) {
      const index = target.indexOf(value);
      if (checked && index === -1) {
        target.push(value);
      } else if (!checked && index !== -1) {
        target.splice(index, 1);
      }
    }
  }
};
</script>
